import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "../components/SiteLayout";
import StoreBadges from "../components/StoreBadges";
import { APP_STORE_URL, EMAIL, PLAY_STORE_URL } from "../site/constants";
import { IconArrow } from "../site/icons";
import "./ChangePasswordPage.css";

function resolveApiHost() {
  const raw = String(
    process.env.REACT_APP_HOST || "https://apiprod.suriwebwinkel.nl"
  ).trim();
  if (!raw) return "https://apiprod.suriwebwinkel.nl";
  if (/^https?:\/\//i.test(raw)) return raw.replace(/\/+$/, "");
  return `https://${raw.replace(/\/+$/, "")}`;
}

const API_HOST = resolveApiHost();

function passwordRules(password) {
  return [
    {
      key: "len",
      label: "Minimaal 6 tekens",
      ok: password.length >= 6,
    },
    {
      key: "cap",
      label: "Minstens 1 hoofdletter",
      ok: /[A-Z]/.test(password),
    },
    {
      key: "special",
      label: "Minstens 1 speciaal teken",
      ok: /[^A-Za-z0-9]/.test(password),
    },
  ];
}

function isPasswordStrong(password) {
  return passwordRules(password).every((rule) => rule.ok);
}

function decodeJwtPayload(token) {
  try {
    const part = String(token || "").split(".")[1];
    if (!part) return null;
    const normalized = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    return JSON.parse(window.atob(padded));
  } catch {
    return null;
  }
}

function isTokenExpired(payload) {
  if (!payload?.exp) return false;
  return Date.now() >= Number(payload.exp) * 1000;
}

function EyeIcon({ open }) {
  return open ? (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M3 3l18 18M10.5 10.7a2.5 2.5 0 003.5 3.5M9.9 5.1A10.5 10.5 0 0121.5 12a10.7 10.7 0 01-4.1 4.7M6.1 6.3A10.6 10.6 0 002.5 12a10.5 10.5 0 0011.4 6.9"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M2.5 12S6.5 5.5 12 5.5 21.5 12 21.5 12 17.5 18.5 12 18.5 2.5 12 2.5 12z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="12"
        r="3.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  show,
  onToggleShow,
  error,
  hint,
}) {
  return (
    <label className="cp-field" htmlFor={id}>
      <span className="cp-field-label">{label}</span>
      <span className={`cp-field-control ${error ? "is-error" : ""}`}>
        <input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          spellCheck={false}
          required
        />
        <button
          type="button"
          className="cp-eye"
          onClick={onToggleShow}
          aria-label={show ? "Verberg wachtwoord" : "Toon wachtwoord"}
        >
          <EyeIcon open={show} />
        </button>
      </span>
      {error ? <span className="cp-field-error">{error}</span> : null}
      {!error && hint ? <span className="cp-field-hint">{hint}</span> : null}
    </label>
  );
}

export default function ChangePasswordPage() {
  const params = useParams();
  const token = useMemo(() => {
    const raw = params.token || "";
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }, [params.token]);
  const payload = useMemo(() => decodeJwtPayload(token), [token]);
  const expired = useMemo(() => isTokenExpired(payload), [payload]);
  const invalidLink = !token || !payload || expired;

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState(false);

  const rules = useMemo(() => passwordRules(password), [password]);
  const strong = isPasswordStrong(password);
  const matches = confirm.length > 0 && confirm === password;
  const canSubmit = strong && matches && !submitting && !invalidLink;

  const firstName = String(payload?.name || "")
    .trim()
    .split(/\s+/)[0];

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (invalidLink) {
      setFormError("Deze link is ongeldig of verlopen.");
      return;
    }
    if (!strong) {
      setFormError("Kies een sterker wachtwoord.");
      return;
    }
    if (!matches) {
      setFormError("Wachtwoorden komen niet overeen.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_HOST}/api/users/change-password-offline`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ newPassword: password }),
      });

      if (res.status === 401) {
        setFormError(
          "Deze link is verlopen of ongeldig. Vraag een nieuwe reset-link aan in de app.",
        );
        return;
      }

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        setFormError(
          data?.message ||
            data?.msg ||
            "Wachtwoord wijzigen is mislukt. Probeer het opnieuw.",
        );
        return;
      }

      setSuccess(true);
      setPassword("");
      setConfirm("");
    } catch {
      setFormError(
        "Geen verbinding met de server. Controleer je internet en probeer opnieuw.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SiteLayout>
      <section className="cp-page">
        <div className="lp-wrap cp-wrap">
          <div className="cp-card" role="main">
            <div className="cp-card-glow" aria-hidden="true" />

            <div className="cp-brand">
              <img src="/images/app-icon.png" alt="" />
              <div>
                <p className="cp-kicker">Suriwebwinkel</p>
                <p className="cp-brand-tag">Het is zo simpel.</p>
              </div>
            </div>

            {success ? (
              <div className="cp-success">
                <div className="cp-success-badge" aria-hidden="true">
                  ✓
                </div>
                <h1>Wachtwoord bijgewerkt</h1>
                <p>
                  Je kunt nu inloggen in de Suriwebwinkel-app met je nieuwe
                  wachtwoord.
                </p>
                <div className="cp-success-actions">
                  <a
                    className="lp-btn-primary"
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open de app
                    <span className="lp-btn-arrow">
                      <IconArrow />
                    </span>
                  </a>
                  <StoreBadges />
                </div>
                <Link to="/" className="cp-text-link">
                  Terug naar de website
                </Link>
              </div>
            ) : invalidLink ? (
              <div className="cp-invalid">
                <div className="cp-invalid-badge" aria-hidden="true">
                  !
                </div>
                <h1>{expired ? "Link verlopen" : "Ongeldige link"}</h1>
                <p>
                  {expired
                    ? "Deze reset-link is niet meer geldig. Vraag in de app een nieuwe ‘wachtwoord vergeten’-mail aan."
                    : "We konden geen geldige reset-link vinden. Open de link opnieuw vanuit je e-mail, of vraag een nieuwe aan in de app."}
                </p>
                <div className="cp-invalid-actions">
                  <a
                    className="lp-btn-primary"
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Naar de app
                    <span className="lp-btn-arrow">
                      <IconArrow />
                    </span>
                  </a>
                  <a className="cp-ghost-btn" href={`mailto:${EMAIL}`}>
                    Hulp nodig? Mail ons
                  </a>
                </div>
              </div>
            ) : (
              <>
                <header className="cp-header">
                  <h1>Nieuw wachtwoord</h1>
                  <p>
                    {firstName
                      ? `Hoi ${firstName}, kies een nieuw wachtwoord voor je Suriwebwinkel-account.`
                      : "Kies een nieuw wachtwoord voor je Suriwebwinkel-account."}
                    {payload?.email ? (
                      <>
                        {" "}
                        We stellen het in voor <strong>{payload.email}</strong>.
                      </>
                    ) : null}
                  </p>
                </header>

                <form className="cp-form" onSubmit={onSubmit} noValidate>
                  <PasswordField
                    id="new-password"
                    label="Nieuw wachtwoord"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (formError) setFormError("");
                    }}
                    autoComplete="new-password"
                    show={showPassword}
                    onToggleShow={() => setShowPassword((v) => !v)}
                  />

                  <ul className="cp-rules" aria-live="polite">
                    {rules.map((rule) => (
                      <li
                        key={rule.key}
                        className={rule.ok ? "is-ok" : undefined}
                      >
                        <span className="cp-rule-dot" aria-hidden="true">
                          {rule.ok ? "✓" : "•"}
                        </span>
                        {rule.label}
                      </li>
                    ))}
                  </ul>

                  <PasswordField
                    id="confirm-password"
                    label="Bevestig wachtwoord"
                    value={confirm}
                    onChange={(e) => {
                      setConfirm(e.target.value);
                      if (formError) setFormError("");
                    }}
                    autoComplete="new-password"
                    show={showConfirm}
                    onToggleShow={() => setShowConfirm((v) => !v)}
                    error={
                      confirm && !matches
                        ? "Wachtwoorden komen niet overeen"
                        : ""
                    }
                    hint={matches ? "Wachtwoorden komen overeen" : ""}
                  />

                  {formError ? (
                    <div className="cp-alert" role="alert">
                      {formError}
                    </div>
                  ) : null}

                  <button
                    type="submit"
                    className="cp-submit"
                    disabled={!canSubmit}
                  >
                    {submitting ? "Bezig met opslaan…" : "Wachtwoord opslaan"}
                  </button>
                </form>

                <p className="cp-footnote">
                  Problemen? Mail <a href={`mailto:${EMAIL}`}>{EMAIL}</a> of
                  download de app via{" "}
                  <a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Google Play
                  </a>
                  .
                </p>
              </>
            )}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
